import { ModelIndexes } from "../../types/addContent";
import { Model, UIImageButton } from "@alphascriptsolutions/client-core";

type YesNoNull = "Yes" | "No" | null;

interface getNextPanelParams {
  currentPanel: number;
  direction: "next" | "back";
  models: Model[];
}

export const getImageButtonValue = (model: Model, name: string): YesNoNull => {
  const button = model.getByName<UIImageButton>(name);
  return button ? (button.value ? "Yes" : "No") : null;
};

export const getAllImageButtonValues = (models: Model[]) => {
  if (!models) return {};
  return {
    isValidIndexation: getImageButtonValue(models[ModelIndexes.TABLE_MODEL_INDEX], "percentageChangeChart"),
    isCustomBuiltIndice: getImageButtonValue(models[ModelIndexes.TABLE_MODEL_INDEX], "addCustomBuiltIndices"),
    isConstantLine: getImageButtonValue(models[ModelIndexes.TABLE_MODEL_INDEX], "addConstantLineSelection"),
    isValidSummaryTable: getImageButtonValue(models[ModelIndexes.CUSTOMIZE_OPTIONS], "addsummaryTable"),
    isValidSmallLineChart: getImageButtonValue(models[ModelIndexes.CUSTOMIZE_OPTIONS], "addSmallLineChart"),
    isValidCallOutBoxes: getImageButtonValue(models[ModelIndexes.CUSTOMIZE_OPTIONS], "addCallOutBoxes"),
    isValidBackgroundShading: getImageButtonValue(models[ModelIndexes.CUSTOMIZE_OPTIONS], "addBackgroundShading"),
    isValidVerticalLine: getImageButtonValue(models[ModelIndexes.CUSTOMIZE_OPTIONS], "addVerticalLine"),
  };
};

export const getNextPanel = ({ currentPanel, direction = "next", models }: getNextPanelParams) => {
  const {
    isValidIndexation,
    isCustomBuiltIndice,
    isConstantLine,
    isValidSummaryTable,
    isValidSmallLineChart,
    isValidCallOutBoxes,
    isValidBackgroundShading,
    isValidVerticalLine,
  } = getAllImageButtonValues(models);

  const transitionMap = {
    [ModelIndexes.CONTENT_LIBRARY]: ModelIndexes.TABLE_MODEL_INDEX,
    [ModelIndexes.TABLE_MODEL_INDEX]:
      isValidIndexation === "Yes"
        ? ModelIndexes.INDEXATION_MODEL_INDEX
        : isCustomBuiltIndice === "Yes"
          ? ModelIndexes.CUSTOM_BUILD_INDEX
          : isConstantLine === "Yes"
            ? ModelIndexes.CONSTANT_LINE
            : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.INDEXATION_MODEL_INDEX]:
      isCustomBuiltIndice === "Yes"
        ? ModelIndexes.CUSTOM_BUILD_INDEX
        : isConstantLine === "Yes"
          ? ModelIndexes.CONSTANT_LINE
          : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.CUSTOM_BUILD_INDEX]:
      isConstantLine === "Yes" ? ModelIndexes.CONSTANT_LINE : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.CONSTANT_LINE]: ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.CUSTOMIZE_OPTIONS]:
      isValidSummaryTable === "Yes"
        ? ModelIndexes.SUMMARY_TABLE
        : isValidSmallLineChart === "Yes"
          ? ModelIndexes.SMALL_LINE_CHART
          : isValidCallOutBoxes === "Yes"
            ? ModelIndexes.CALL_OUT_BOXES
            : isValidBackgroundShading === "Yes"
              ? ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL
              : isValidVerticalLine === "Yes"
                ? ModelIndexes.VERTICAL_LINE_TABLE_PANEL
                : ModelIndexes.FORMAT_TEMPLATE,

    [ModelIndexes.SUMMARY_TABLE]:
      isValidSmallLineChart === "Yes"
        ? ModelIndexes.SMALL_LINE_CHART
        : isValidCallOutBoxes === "Yes"
          ? ModelIndexes.CALL_OUT_BOXES
          : isValidBackgroundShading === "Yes"
            ? ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL
            : isValidVerticalLine === "Yes"
              ? ModelIndexes.VERTICAL_LINE_TABLE_PANEL
              : ModelIndexes.FORMAT_TEMPLATE,

    [ModelIndexes.SMALL_LINE_CHART]:
      isValidCallOutBoxes === "Yes"
        ? ModelIndexes.CALL_OUT_BOXES
        : isValidBackgroundShading === "Yes"
          ? ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL
          : isValidVerticalLine === "Yes"
            ? ModelIndexes.VERTICAL_LINE_TABLE_PANEL
            : ModelIndexes.FORMAT_TEMPLATE,

    [ModelIndexes.CALL_OUT_BOXES]:
      isValidBackgroundShading === "Yes"
        ? ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL
        : isValidVerticalLine === "Yes"
          ? ModelIndexes.VERTICAL_LINE_TABLE_PANEL
          : ModelIndexes.FORMAT_TEMPLATE,

    [ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL]:
      isValidVerticalLine === "Yes" ? ModelIndexes.VERTICAL_LINE_TABLE_PANEL : ModelIndexes.FORMAT_TEMPLATE,

    [ModelIndexes.FORMAT_TEMPLATE]: ModelIndexes.FORMAT_TEMPLATE,
  };

  const reverseTransitionMap = {
    [ModelIndexes.FORMAT_TEMPLATE]:
      isValidVerticalLine === "Yes"
        ? ModelIndexes.VERTICAL_LINE_TABLE_PANEL
        : isValidBackgroundShading === "Yes"
          ? ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL
          : isValidCallOutBoxes === "Yes"
            ? ModelIndexes.CALL_OUT_BOXES
            : isValidSmallLineChart === "Yes"
              ? ModelIndexes.SMALL_LINE_CHART
              : isValidSummaryTable === "Yes"
                ? ModelIndexes.SUMMARY_TABLE
                : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.VERTICAL_LINE_TABLE_PANEL]:
      isValidBackgroundShading === "Yes"
        ? ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL
        : isValidCallOutBoxes === "Yes"
          ? ModelIndexes.CALL_OUT_BOXES
          : isValidSmallLineChart === "Yes"
            ? ModelIndexes.SMALL_LINE_CHART
            : isValidSummaryTable === "Yes"
              ? ModelIndexes.SUMMARY_TABLE
              : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL]:
      isValidCallOutBoxes === "Yes"
        ? ModelIndexes.CALL_OUT_BOXES
        : isValidSmallLineChart === "Yes"
          ? ModelIndexes.SMALL_LINE_CHART
          : isValidSummaryTable === "Yes"
            ? ModelIndexes.SUMMARY_TABLE
            : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.CALL_OUT_BOXES]:
      isValidSmallLineChart === "Yes"
        ? ModelIndexes.SMALL_LINE_CHART
        : isValidSummaryTable === "Yes"
          ? ModelIndexes.SUMMARY_TABLE
          : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.SMALL_LINE_CHART]:
      isValidSummaryTable === "Yes" ? ModelIndexes.SUMMARY_TABLE : ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.SUMMARY_TABLE]: ModelIndexes.CUSTOMIZE_OPTIONS,

    [ModelIndexes.CUSTOMIZE_OPTIONS]:
      isConstantLine === "Yes"
        ? ModelIndexes.CONSTANT_LINE
        : isCustomBuiltIndice === "Yes"
          ? ModelIndexes.CUSTOM_BUILD_INDEX
          : isValidIndexation === "Yes"
            ? ModelIndexes.INDEXATION_MODEL_INDEX
            : ModelIndexes.TABLE_MODEL_INDEX,

    [ModelIndexes.CONSTANT_LINE]:
      isCustomBuiltIndice === "Yes"
        ? ModelIndexes.CUSTOM_BUILD_INDEX
        : isValidIndexation === "Yes"
          ? ModelIndexes.INDEXATION_MODEL_INDEX
          : ModelIndexes.TABLE_MODEL_INDEX,

    [ModelIndexes.CUSTOM_BUILD_INDEX]:
      isValidIndexation === "Yes" ? ModelIndexes.INDEXATION_MODEL_INDEX : ModelIndexes.TABLE_MODEL_INDEX,

    [ModelIndexes.INDEXATION_MODEL_INDEX]: ModelIndexes.TABLE_MODEL_INDEX,
  };

  return direction === "next"
    ? (transitionMap[currentPanel] ?? currentPanel + 1)
    : (reverseTransitionMap[currentPanel] ?? currentPanel - 1);
};

export const setAllModelNavigationBtns = (
  models: any[],
  handleBack: Function,
  handleNext: Function,
  handleClosePopup: Function
) => {
  models.forEach((model, index) => {
    const backBtn = model.getByName("backBtn");
    const nextBtn = model.getByName("nextBtn");
    const cancelBtn = model.getByName("btnCancel");
    const btnContentLibraryCancel = model.getByName("btnContentLibraryCancel");

    if (backBtn && index !== 0) backBtn.onClick = () => handleBack(index);
    if (nextBtn && index !== models.length - 1) nextBtn.onClick = () => handleNext(index);
    if (cancelBtn && index !== 0) cancelBtn.onClick = handleClosePopup;
    if (btnContentLibraryCancel && index === 0) btnContentLibraryCancel.onClick = handleClosePopup;
  });
};
